import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { BankAccountType } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { SupplierInvoiceGroup } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/supplier-invoice-group';
import { SupplierInvoiceGroupItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/supplier-invoice-group-item';
import {
    CurrencyCode,
    InvoiceFundingStatus2,
    Money,
    PayablesInput,
    PayablesResponse,
    Query,
    TransferType
} from '../../model/models';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { mapPayableStatusLegacyToInvoiceFundingStatus } from '../../helpers/status-mapping';
import { subtractMoney, sumMonies } from '../../helpers/money-operations';

export class GetSupplierInvoiceGroupRequest extends Mk2Request {
    invoiceKey?: string;

    constructor(initial?: Partial<GetSupplierInvoiceGroupRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class GetSupplierInvoiceGroupResponse extends Mk2Response {
    invoiceGroup?: SupplierInvoiceGroup;
    message?: string;
    ok?: boolean;

    constructor(initial?: Partial<GetSupplierInvoiceGroupResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class GetSupplierInvoiceGroupService extends ApiService<
    GetSupplierInvoiceGroupRequest,
    GetSupplierInvoiceGroupResponse
> {
    public requestType = GetSupplierInvoiceGroupRequest;
    public responseType = GetSupplierInvoiceGroupResponse;
    public endpointName = 'supplier/InvoiceGroup/GetSupplierInvoiceGroup';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'supplier/InvoiceGroup/GetSupplierInvoiceGroup';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetSupplierInvoiceGroupRequest
    ): { query: string } | GetSupplierInvoiceGroupRequest {
        if (this._isPayLaterEnabled) {
            const input: PayablesInput = {
                payableIds: [+req.invoiceKey],
                companyFilter: {
                    supplierIds: []
                }
            };
            const money = { cents: true, currencyCode: true };
            const query: Query = {
                payables: {
                    __args: { input },
                    rows: {
                        statusLegacy: true,
                        dueInDays: true,
                        adjustedDueDate: true,
                        buyerDiscountFee: money,
                        supplierDiscountFee: money,
                        // relationship: {
                        //     scfConfig: {
                        //         isBuyerPaidEarlyPaymentFeePercentageVisible: true
                        //     }
                        // },
                        approvals: {
                            id: true,
                            amount: money,
                            statusLegacy: true,
                            invoice: {
                                invoiceNumber: true,
                                date: true,
                                terms: true
                            }
                        },
                        creditMemos: {
                            id: true,
                            creditMemoNumber: true,
                            date: true,
                            amount: money,
                            allocatedAmount: money
                        },
                        transfers: {
                            transferType: true,
                            transferDate: true,
                            payeeBankAccount: {
                                bankName: true,
                                accountNumber: true
                            },
                            supplierDiscountFee: money,
                            supplierPaymentAmount: money
                        }
                    }
                }
            };
            return { query: `{${jsonToGraphQLQuery(query)}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | GetSupplierInvoiceGroupResponse
    ): GetSupplierInvoiceGroupResponse {
        if (this._isPayLaterEnabled) {
            const { rows } = (res as GraphQLResponse<PayablesResponse>).data.payables;
            const payable = rows[0];
            const { approvals, creditMemos } = payable;
            const creditsAmount = payable.creditMemos.map((cm) => cm.allocatedAmount);
            const creditsSum = this._sumMonies(creditsAmount);
            const transfer = payable.transfers[0];
            const bankAccount = transfer?.payeeBankAccount;
            const bankAccountType = this._translateBankAccountType(transfer?.transferType);

            const buyerPaidTransactionFeeVisible =
                payable.relationship?.scfConfig?.isBuyerPaidEarlyPaymentFeePercentageVisible ??
                false;

            const totalInvoiceAmount = sumMonies(approvals.map((a) => a.amount));

            return new GetSupplierInvoiceGroupResponse({
                ok: true,
                invoiceGroup: {
                    bankAccountType,
                    buyerPaidTransactionFeeVisible,
                    buyerDiscountAmount: payable.buyerDiscountFee,
                    credits: creditsSum,
                    feeRate: payable.supplierDiscountFeeRate,
                    fees: this._sumMonies(payable.transfers.map((t) => t.supplierDiscountFee)),
                    items: [
                        ...approvals.map(
                            (approval) =>
                                <SupplierInvoiceGroupItem>{
                                    itemKey: approval.id.toString(),
                                    status: mapPayableStatusLegacyToInvoiceFundingStatus(
                                        approval.statusLegacy
                                    ),
                                    invoiceNumber: approval.invoice.invoiceNumber,
                                    invoiceDate: approval.invoice.date,
                                    dueIn: approval.invoice.terms,
                                    dueDate: payable.adjustedDueDate,
                                    invoiceAmount: approval.amount,
                                    buyerDiscountAmount: payable.buyerDiscountFee,
                                    supplierDiscountAmount: payable.supplierDiscountFee
                                }
                        ),
                        ...creditMemos.map((creditMemo) => {
                            let creditMemoAllocation = creditMemo.allocatedAmount;
                            creditMemoAllocation.cents = -creditMemoAllocation.cents;
                            return <SupplierInvoiceGroupItem>{
                                itemKey: creditMemo.id.toString(),
                                status: InvoiceFundingStatus2.CREDIT_MEMO,
                                invoiceNumber: creditMemo.creditMemoNumber,
                                invoiceDate: creditMemo.date,
                                dueIn: payable.dueInDays,
                                dueDate: payable.adjustedDueDate,
                                invoiceAmount: creditMemoAllocation,
                                buyerDiscountAmount: payable.buyerDiscountFee,
                                supplierDiscountAmount: payable.supplierDiscountFee
                            };
                        })
                    ],
                    paymentDate: transfer?.transferDate,
                    paymentMethod: bankAccount
                        ? `${bankAccount.bankName} (${bankAccount.accountNumber})`
                        : '',
                    paymentTotal: this._sumMonies(
                        payable.transfers.map((t) => t.supplierPaymentAmount)
                    ),
                    status: mapPayableStatusLegacyToInvoiceFundingStatus(payable.statusLegacy),
                    supplierDiscountAmount: payable.supplierDiscountFee,
                    totalInvoiceAmount,
                    total: subtractMoney(totalInvoiceAmount, creditsSum)
                }
            });
        }
        return res as GetSupplierInvoiceGroupResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    // TODO: translate to proper status enum
    private _translateBankAccountType(transferType: TransferType): BankAccountType {
        return BankAccountType[transferType] || BankAccountType.UNKNOWN_BANK_ACCOUNT_TYPE;
    }

    private _sumMonies(monies: Money[]): Money {
        const cents = monies
            .map((money) => money.cents)
            .reduce((prev, curr) => {
                return prev + curr;
            }, 0);

        return {
            cents,
            currencyCode: monies[0]?.currencyCode || CurrencyCode.UNKNOWN_CURRENCY_CODE
        };
    }
}
